import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Users, Mail, Download, Search, Play, Pause, Lock, Filter, Loader2, Edit2 } from 'lucide-react';
import { crmMailingListsApi, CrmMailingList } from '../services/crmApi';
import { toast } from 'sonner';
export function MailingListDetail() {
  const {
    listId
  } = useParams<{
    listId: string;
  }>();
  const [list, setList] = useState<CrmMailingList | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'subscribers' | 'submissions'>('subscribers');
  useEffect(() => {
    const fetchList = async () => {
      if (!listId) return;
      setLoading(true);
      try {
        const data = await crmMailingListsApi.get(listId);
        setList(data);
      } catch (err) {
        console.error('Failed to fetch list:', err);
        toast.error('Failed to load mailing list details');
      } finally {
        setLoading(false);
      }
    };
    fetchList();
  }, [listId]);
  const getIntakeStatusConfig = (status: string) => {
    switch (status) {
      case 'open':
        return {
          icon: Play,
          color: 'text-green-600',
          bg: 'bg-green-50',
          label: 'Accepting Signups'
        };
      case 'paused':
        return {
          icon: Pause,
          color: 'text-yellow-600',
          bg: 'bg-yellow-50',
          label: 'Paused'
        };
      case 'closed':
        return {
          icon: Lock,
          color: 'text-red-600',
          bg: 'bg-red-50',
          label: 'Closed'
        };
      default:
        return {
          icon: Play,
          color: 'text-gray-600',
          bg: 'bg-gray-50',
          label: 'Unknown'
        };
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4912">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4913" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4914">
          Loading list details...
        </span>
      </div>;
  }
  if (!list) {
    return <div className="text-center py-12" data-id="element-4915">
        <h2 className="text-lg font-medium text-gray-900" data-id="element-4916">
          Mailing list not found
        </h2>
        <Link to="/mailing-lists" className="text-blue-600 hover:text-blue-800 mt-2 inline-block" data-id="element-4917">
          ← Back to Mailing Lists
        </Link>
      </div>;
  }
  const intakeConfig = getIntakeStatusConfig(list.Status);
  const IntakeIcon = intakeConfig.icon;
  return <div className="space-y-6" data-id="element-4918">
      {/* Header */}
      <div className="flex items-start justify-between" data-id="element-4919">
        <div className="flex items-start gap-4" data-id="element-4920">
          <Link to="/mailing-lists" className="p-2 hover:bg-gray-100 rounded-full text-gray-500 mt-1" data-id="element-4921">
            <ArrowLeft className="h-5 w-5" data-id="element-4922" />
          </Link>
          <div data-id="element-4923">
            <div className="flex items-center gap-3" data-id="element-4924">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4925">
                {list.Name}
              </h1>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${intakeConfig.bg} ${intakeConfig.color}`} data-id="element-4926">
                <IntakeIcon className="h-3 w-3" data-id="element-4927" />
                {intakeConfig.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-4928">
              {list.Description || 'No description'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-4929">
          <button className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-4930">
            <Edit2 className="h-4 w-4" data-id="element-4931" />
            Edit
          </button>
          <button className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-4932">
            <Download className="h-4 w-4" data-id="element-4933" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4" data-id="element-4934">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4935">
          <div className="flex items-center gap-3" data-id="element-4936">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-4937">
              <Users className="h-5 w-5 text-blue-600" data-id="element-4938" />
            </div>
            <div data-id="element-4939">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-4940">
                {(list.Subscriber_Count || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500" data-id="element-4941">Total Subscribers</p>
            </div>
          </div>
        </div>
        {/* Placeholder stats for now since we don't have subscriber details from CRM yet */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 opacity-50" data-id="element-4942">
          <div className="flex items-center gap-3" data-id="element-4943">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-4944">
              <Mail className="h-5 w-5 text-green-600" data-id="element-4945" />
            </div>
            <div data-id="element-4946">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-4947">-</p>
              <p className="text-xs text-gray-500" data-id="element-4948">Confirmed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-4949">
        <nav className="-mb-px flex gap-6" data-id="element-4950">
          <button onClick={() => setActiveTab('subscribers')} className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'subscribers' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4951">
            Subscribers
          </button>
          <button onClick={() => setActiveTab('submissions')} className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'submissions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4952">
            Form Submissions
          </button>
        </nav>
      </div>

      {/* Content Placeholder */}
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4953">
        <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-4954" />
        <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-4955">
          Subscriber data syncing...
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-4956">
          Detailed subscriber lists are being synced from Zoho CRM.
        </p>
      </div>
    </div>;
}